import { useState } from 'react'
import { ensureAnonSession, lookupRoomByCode } from '../lib/api/tv'
import { joinRoomByCode } from '../lib/api/rooms'
import { fetchWordTvRoom } from '../lib/api/wordTv'
import {
  fetchColorfulRoomByCode, joinColorfulRoomByCode,
} from '../games/colorful/api/rooms'
import {
  fetchThreeStepRoomByCode, joinThreeStepRoomByCode,
} from '../games/three-step/api/rooms'
import { readError } from '../lib/errors'
import { useT } from '../i18n'

export type JoinedGame = 'ideatthat' | 'colorful' | 'three_step'

interface Props {
  presetCode: string | null
  onJoined: (game: JoinedGame, roomId: string, roomCode: string) => void
}

/**
 * Phone-side entry: enter the room code (or have it prefilled from
 * /join/CODE) and a display name. No account required — we create an
 * anonymous Supabase session under the hood.
 */
export function JoinHomeScreen({ presetCode, onJoined }: Props) {
  const t = useT()
  const [code, setCode] = useState(presetCode ?? '')
  // Remember the name from a previous join so a player who dropped and
  // comes back doesn't have to retype it (the room re-attaches them to
  // their existing seat by identity regardless).
  const [name, setName] = useState(() => localStorage.getItem('jdg_player_name') ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = code.trim().length >= 4 && name.trim().length >= 1 && !busy

  async function handleJoin() {
    setBusy(true); setError(null)
    try {
      const trimmedCode = code.trim().toUpperCase()
      const trimmedName = name.trim()
      // Persist for next time (drop → rejoin without retyping).
      if (trimmedName) localStorage.setItem('jdg_player_name', trimmedName)

      // Sign in first — RLS requires auth even for the room-code lookup.
      await ensureAnonSession(trimmedName)

      // Try IET, then Colorful, then Three Step. The code namespace is
      // shared across all three game tables (the per-game code generators
      // collision-check against each other), so exactly one table has
      // the row. Three Step was missing from this lookup before — that's
      // why /join hosted-from-Three-Step rooms returned "No room with
      // that code" even when the room existed.
      const ietPreview = await lookupRoomByCode(trimmedCode)
      if (ietPreview) {
        const joined = await joinRoomByCode(trimmedCode)
        onJoined('ideatthat', joined.id, joined.code)
        return
      }

      const colorfulPreview = await fetchColorfulRoomByCode(trimmedCode)
      if (colorfulPreview) {
        const joined = await joinColorfulRoomByCode(trimmedCode)
        onJoined('colorful', joined.id, joined.code)
        return
      }

      const threeStepPreview = await fetchThreeStepRoomByCode(trimmedCode)
      if (threeStepPreview) {
        const joined = await joinThreeStepRoomByCode(trimmedCode)
        onJoined('three_step', joined.id, joined.code)
        return
      }

      // Word-game rooms are played in the main app, not here: the seat
      // is a normal endless session and the whole game UI already
      // exists there. Hand the code over rather than growing a second
      // copy of that screen inside the join shell. The anon session
      // created above rides along (same origin), so the main app skips
      // the auth screen instead of bouncing the guest.
      const wordRoom = await fetchWordTvRoom(trimmedCode)
      if (wordRoom) {
        const slug = wordRoom.game === 'close_enough' ? 'close-enough' : 'fake-out'
        window.location.href = `/?wordjoin=${wordRoom.code}&game=${slug}`
        return
      }

      throw new Error('No room with that code. Double-check the host screen?')
    } catch (e) {
      setError(readError(e))
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      <div className="flex-1 flex flex-col justify-center px-6 py-10 max-w-md mx-auto w-full">
        {/* Back arrow — lives inside the content column so it lines
            up with the form below. `/join` is a separate top-level
            app entry; a plain nav to `/` drops users back into the
            main app (auth screen or hub). */}
        <button
          onClick={() => { window.location.href = '/' }}
          className="self-start w-10 h-10 mb-4 flex items-center justify-center rounded-full
                     bg-brand-elevated active:bg-brand-border transition-colors"
          aria-label={t('Back')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="#111" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="text-center mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2">{t('Party Mode')}</p>
          <h1 className="font-display text-5xl font-bold text-brand-text leading-none">{t('Join the Game')}</h1>
          <p className="font-display text-lg text-brand-muted mt-2">{t('Enter the code on the TV')}</p>
        </div>

        <label className="block mb-5">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{t('Room Code')}</span>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="ABCDEF"
            autoCapitalize="characters"
            autoComplete="off"
            inputMode="text"
            className="mt-1 w-full px-4 py-4 rounded-2xl border border-brand-border
                       bg-white text-brand-text font-display text-3xl tracking-[0.2em]
                       text-center uppercase
                       focus:outline-hidden focus:border-brand-primary
                       focus:ring-2 focus:ring-brand-primary/20"
          />
        </label>

        <label className="block mb-6">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{t('Your Name')}</span>
          <input
            value={name}
            onChange={e => setName(e.target.value.slice(0, 20))}
            placeholder="Jordan"
            autoComplete="name"
            className="mt-1 w-full px-4 py-4 rounded-2xl border border-brand-border
                       bg-white text-brand-text text-xl
                       focus:outline-hidden focus:border-brand-primary
                       focus:ring-2 focus:ring-brand-primary/20"
          />
        </label>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-4">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        <button
          onClick={handleJoin}
          disabled={!canSubmit}
          className="btn-primary disabled:opacity-40 text-lg py-4"
        >
          {busy ? t('Joining…') : t('Join Party')}
        </button>

        <p className="text-xs text-brand-muted text-center mt-6">{t("No account needed. You're in the game as soon as you join.")}</p>
      </div>
    </div>
  )
}
